import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Button, Alert } from '@mui/material';
import { PlayArrow, VolumeUp } from '@mui/icons-material';
import { LessonStep } from '../../types';
import { playAudio } from '../../../../utils/audioUtils';
import TermAnnotation from '../TermAnnotation';
import StepNavigation from '../StepNavigation';
import { useLessonTerms } from '../../hooks/useLessonTerms';

interface IntroductionStepProps {
  step: LessonStep;
  isLastStep?: boolean;
  token: string | null;
  onNext: (score?: number) => void;
  onBackToLetters?: () => void;
  lessonId?: string;
}

const IntroductionStep: React.FC<IntroductionStepProps> = ({ step, isLastStep = false, token, onNext, onBackToLetters, lessonId }) => {
  const [lessonTerms, setLessonTerms] = useState<any[]>([]);
  const { getLessonTerms, error: termsError } = useLessonTerms();

  // Загружаем термины урока при монтировании компонента
  useEffect(() => {
    const loadLessonTerms = async () => {
      if (lessonId) {
        try {
          const terms = await getLessonTerms(lessonId);
          setLessonTerms(terms);
        } catch (error) {
          console.error('Ошибка загрузки терминов урока:', error);
        }
      }
    };

    loadLessonTerms();
  }, [lessonId, getLessonTerms]);

  const handlePlayAudio = async () => {
    if (step.content.audio_url) {
      try {
        await playAudio(step.content.audio_url, token);
      } catch (error) {
        console.error('Ошибка воспроизведения аудио:', error);
      }
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {step.title}
        </Typography>
        
        {step.content.instruction && (
          <Box sx={{ mb: 3 }}>
            <TermAnnotation 
              text={step.content.instruction}
              terms={lessonTerms}
            />
          </Box>
        )}
        
        {termsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Не удалось загрузить термины для аннотации: {termsError}
          </Alert>
        )}

        {step.content.name_ar && (
          <Box sx={{textAlign: 'center', my: 3}}>
            <Typography dir="rtl" sx={{fontFamily: 'Noto Naskh Arabic, serif', fontSize: 64}}>{step.content.letter}</Typography>
            <Typography dir="rtl" variant="h5">{step.content.name_ar}</Typography>
            {step.content.name_transliteration && <Typography>Транслитерация имени: {step.content.name_transliteration}</Typography>}
            {step.content.forms && typeof step.content.forms === 'object' && (
              <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, my: 2}}>
                {Object.entries(step.content.forms).filter(([, value]) => typeof value === 'string').map(([form, value]) => (
                  <Box key={form}><Typography variant="caption">{({isolated: 'Отдельная', initial: 'Начальная', medial: 'Срединная', final: 'Конечная'} as Record<string,string>)[form] || form}</Typography><Typography dir="rtl" sx={{fontSize: 40}}>{String(value)}</Typography></Box>
                ))}
              </Box>
            )}
            {step.content.source?.names?.license && <Typography variant="caption" component="p">Имена: Wiktionary, {step.content.source.names.license}. Формы: Unicode 17. Показаны только опубликованные формы.</Typography>}
          </Box>
        )}
        {step.content.individual_letter_audio_available === false && <Alert severity="info" sx={{mb: 2}}>В этом справочном шаге нет записи отдельной буквы. Оценка произношения не выполняется.</Alert>}
        
        {step.content.svg_url && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img 
              src={step.content.svg_url} 
              alt={step.title}
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                maxHeight: '300px'
              }}
            />
          </Box>
        )}
        
        {step.content.audio_url && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<VolumeUp />}
              onClick={handlePlayAudio}
              size="large"
            >
              Прослушать
            </Button>
          </Box>
        )}
        
        {isLastStep && <Alert severity="info" sx={{mb: 2}}>Это заключительный шаг урока. После нажатия сохранится ваша отметка об ознакомлении без оценки навыка.</Alert>}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            endIcon={<PlayArrow />}
            onClick={() => onNext()}
            size="large"
          >
            {isLastStep ? 'Отметить ознакомление' : 'Продолжить урок'}
          </Button>
        </Box>

        <StepNavigation
          onBackToLetters={onBackToLetters}
          showBackToLetters={true}
        />
      </CardContent>
    </Card>
  );
};

export default IntroductionStep;
